import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { FOCUS_RING_CLASSES, trapTabKey } from '../lib/focusTrap';
import { useScrollLock } from '../hooks/useScrollLock';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Accessible name for the dialog panel. */
  ariaLabel: string;
  /** Accessible name for the close button. */
  closeLabel: string;
  /** Rendered left of the close button in the drawer header. */
  headerContent?: React.ReactNode;
  /** data-testid on the click-to-close overlay. */
  overlayTestId?: string;
  children: React.ReactNode;
}

/**
 * The app's modal slide-out drawer: left panel, overlay, scroll lock, focus
 * trap, and inert-when-closed — extracted from SideNav so the documents
 * file-tree drawer shares the exact same mechanics. Only one drawer can be
 * open at a time by construction: each is aria-modal with its own overlay, and
 * their triggers live on surfaces the other's overlay covers.
 */
export function Drawer({
  isOpen,
  onClose,
  ariaLabel,
  closeLabel,
  headerContent,
  overlayTestId,
  children,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<Element | null>(null);
  const wasOpenRef = useRef(false);

  useScrollLock(isOpen);

  // Capture the element focused when the drawer opens, synchronously as it
  // opens, so we can hand focus back to the trigger on close.
  if (isOpen && !wasOpenRef.current) {
    triggerRef.current = document.activeElement;
  }
  wasOpenRef.current = isOpen;

  // The closed drawer stays in the DOM (off-screen) — mark it inert so it is
  // unreachable by Tab/screen readers. React 18 doesn't type the `inert` prop,
  // so toggle the native attribute via the ref. Inert must flip BEFORE the
  // focus move below (focusing inside an inert subtree is ignored).
  // Then: initial focus on the Close button on open; restore the trigger on close.
  useEffect(() => {
    panelRef.current?.toggleAttribute('inert', !isOpen);
    if (isOpen) {
      closeButtonRef.current?.focus();
      return;
    }
    const trigger = triggerRef.current as HTMLElement | null;
    if (trigger && document.contains(trigger) && typeof trigger.focus === 'function') {
      trigger.focus();
    }
    triggerRef.current = null;
  }, [isOpen]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onClose();
      return;
    }
    trapTabKey(e, panelRef.current);
  };

  return (
    <>
      <div
        data-testid={overlayTestId}
        className={`fixed inset-0 bg-slate-900/30 backdrop-blur-sm z-40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-hidden
      />

      <div
        ref={panelRef}
        className={`fixed inset-y-0 left-0 w-72 sm:w-80 bg-[var(--surface)] shadow-2xl z-50 transform transition-transform duration-300 safe-top safe-bottom ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}
        onKeyDown={handleKeyDown}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        tabIndex={-1}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-[var(--card-border)]">
            <div className="flex items-center justify-between">
              {headerContent}
              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className={`p-2 hover:bg-[color-mix(in_oklab,var(--text-primary)_7%,transparent)] rounded-full transition-colors ${FOCUS_RING_CLASSES}`}
                aria-label={closeLabel}
              >
                <X className="w-5 h-5 text-[var(--text-secondary)]" aria-hidden />
              </button>
            </div>
          </div>
          {children}
        </div>
      </div>
    </>
  );
}
